import React, { useState, useEffect } from "react";
import { PushToTalkButton } from "./PushToTalkButton";
import { TranscriptPreview } from "./TranscriptPreview";
import { WorkflowFooter } from "./WorkflowFooter";
import { useSpeechRecognition } from "../hooks/useSpeechRecognition";

// Recording states
const STATE = {
  IDLE: "idle",
  RECORDING: "recording",
  REVIEW: "review",
};

export function QuestionScreen({
  question,
  questionIndex,
  isLoading,
  onSubmitAnswer,
  onReviewAnswers,
  collectedAnswers,
  onHome,
  onBack,
  error,
  onClearError,
}) {
  const [recordState, setRecordState] = useState(STATE.IDLE);
  const [editableTranscript, setEditableTranscript] = useState("");

  const {
    isListening,
    transcript: speechTranscript,
    error: speechError,
    isSupported,
    startListening,
    stopListening,
    clearTranscript,
  } = useSpeechRecognition();

  // Merge speech output into editable copy
  useEffect(() => {
    if (speechTranscript) {
      setEditableTranscript(speechTranscript);
    }
  }, [speechTranscript]);

  // Reset on question change
  useEffect(() => {
    setRecordState(STATE.IDLE);
    setEditableTranscript("");
    clearTranscript();
  }, [question?.id, clearTranscript]);

  // Move to REVIEW when recognition ends
  useEffect(() => {
    if (!isListening && recordState === STATE.RECORDING) {
      setRecordState(STATE.REVIEW);
    }
  }, [isListening, recordState]);

  const handlePTTPress = () => {
    if (recordState === STATE.IDLE) {
      setRecordState(STATE.RECORDING);
      startListening();
    } else if (recordState === STATE.RECORDING) {
      stopListening();
    }
  };

  const handleConfirm = () => {
    onSubmitAnswer(editableTranscript);
  };

  const handleContinueListening = () => {
    setRecordState(STATE.RECORDING);
    startListening();
  };

  const handleRetry = () => {
    stopListening();
    clearTranscript();
    setEditableTranscript("");
    setRecordState(STATE.IDLE);
  };

  const handleCancel = () => {
    stopListening();
    clearTranscript();
    setEditableTranscript("");
    setRecordState(STATE.IDLE);
  };

  const displayError = error || speechError;
  const hasCollectedAnswers = collectedAnswers && collectedAnswers.length > 0;

  return (
    <div className="screen question-screen nx-screen">

      {/* TOP ACTION BAR */}
      <div className="q-action-bar">
        <div className="q-action-bar-left">
          <div className="progress-bar nx-progress-bar">
            <div className="progress-track nx-progress-track">
              <div className="progress-fill nx-progress-fill" />
            </div>
            <span className="progress-label nx-progress-label">
              Q{questionIndex + 1}
            </span>
          </div>
        </div>

        <div className="q-action-bar-right">
          <button
            className="q-op-btn q-op-btn-review"
            onClick={onReviewAnswers}
            aria-label="Review your answers"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
            Review
            {hasCollectedAnswers && (
              <span className="q-op-btn-badge">{collectedAnswers.length}</span>
            )}
          </button>
        </div>
      </div>

      {/* Question card */}
      <div className="question-card nx-question-card">
        {isLoading ? (
          <div className="question-loading nx-question-loading">
            <span className="nx-spinner nx-spinner-lg" />
            <p>Loading next question…</p>
          </div>
        ) : (
          <>
            <p className="question-number nx-question-number">
              Question {questionIndex + 1}
            </p>
            <h2 className="question-text nx-question-text">{question?.text}</h2>
          </>
        )}
      </div>

      {/* Error display */}
      {displayError && (
        <div className="error-banner nx-error-banner" role="alert">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="error-icon">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <p>{displayError}</p>
          <button className="error-dismiss" onClick={onClearError} aria-label="Dismiss error">✕</button>
        </div>
      )}

      {/* Browser support warning */}
      {!isSupported && (
        <div className="error-banner nx-error-banner" role="alert">
          <p>Voice input is not supported in your browser. Please use Chrome or Safari.</p>
        </div>
      )}

      {/* Main interaction area */}
      {!isLoading && (
        <div className="interaction-area">
          {recordState !== STATE.REVIEW ? (
            <PushToTalkButton
              isListening={isListening}
              onPress={handlePTTPress}
              disabled={!isSupported || isLoading}
            />
          ) : (
            <TranscriptPreview
              transcript={editableTranscript}
              onTranscriptChange={setEditableTranscript}
              isLoading={isLoading}
              onConfirm={handleConfirm}
              onContinue={handleContinueListening}
              onRetry={handleRetry}
              onBack={handleCancel}
              onCancel={handleCancel}
            />
          )}
        </div>
      )}

      {/* Instruction hint — idle only */}
      {recordState === STATE.IDLE && !isLoading && (
        <p className="instruction-hint nx-instruction-hint">
          Tap the button above, speak your answer, then tap again to stop.
        </p>
      )}

      {/* Bottom workflow navigation — idle only */}
      {recordState === STATE.IDLE && (
        <WorkflowFooter
          onBack={onBack}
          onHome={onHome}
          showBack={true}
          backLabel="Back"
          homeLabel="Home"
        />
      )}

    </div>
  );
}
